import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Snackbar, AppUser } from '@models/models';
import { CredentialsApiService } from '@services/api-calls/credentials-api.service';
import { MustMatch } from '@providers/validators/must-match';
import { TranslateService } from '@ngx-translate/core';
import { HelpersService } from 'app/providers/helpers/helpers';
import { SnackbarService } from '@services/snackbar.service';
import { StoreUser } from '@providers/store/user.service';

@Component({
    selector: 'app-edit-profile',
    templateUrl: './edit-profile.component.html',
    styleUrls: ['./edit-profile.component.scss'],
})
export class EditProfileComponent implements OnInit {
    public editUserForm: FormGroup;
    public editPasswordForm: FormGroup;
    public submitted: boolean = false;
    public submittedPassword: boolean = false;

    public userAvatar: any = 'assets/images/fake-avatar.svg';
    public avatarFile: File;

    public show: boolean = false;
    public show_password_form: boolean = false;

    public user: AppUser = null;
    public errorFile: boolean = false;
    public imagePath: string = '';

    public snackbar: Snackbar;

    public firstTime = true;
    public passwordValidatorFeedback: any[] = [];
    public profile_feedback: any;

    @Output() callback = new EventEmitter<any>();

    constructor(
        private formBuilder: FormBuilder,
        private _credentialsApiService: CredentialsApiService,
        private translate: TranslateService,
        private helper: HelpersService,
        private snackbarService: SnackbarService,
        private userStore: StoreUser
    ) {}

    ngOnInit() {
        this.snackbar = this.snackbarService.snackbarItem;
        this.translate
            .get('EDITPROFILE.validPassword')
            .subscribe((translated: string) => {
                this.profile_feedback = {
                    validPassword: translated,
                    successChangedPassword: this.translate.instant(
                        'EDITPROFILE.successChangedPassword'
                    ),
                    passwordDoesntMatch: this.translate.instant(
                        'EDITPROFILE.passwordDoesntMatch'
                    ),
                    somethingWentWrong: this.translate.instant(
                        'EDITPROFILE.somethingWentWrong'
                    ),
                    userEmailAlreadyExists: this.translate.instant(
                        'EDITPROFILE.userEmailAlreadyExists'
                    ),
                    updateUserSuccess: this.translate.instant(
                        'EDITPROFILE.updateUserSuccess'
                    ),
                };
            });

        this.editPasswordForm = this.formBuilder.group(
            {
                old_password: ['', Validators.required],
                new_password: [
                    '',
                    [Validators.required, Validators.minLength(7)],
                ],
                new_password_repeat: ['', Validators.required],
            },
            {
                validator: MustMatch('new_password', 'new_password_repeat'),
            }
        );
    }

    fillUserData() {
        if (!this.editUserForm) {
            this.editUserForm = this.formBuilder.group({});
        }
        if (this._credentialsApiService.currentAppUserValue) {
            this.user = this._credentialsApiService.currentAppUserValue;
            setTimeout(() => {
                this.editUserForm.controls.username.setValue(
                    this.user['first_name']
                );
                this.editUserForm.controls.email.setValue(this.user['email']);
            }, 10);
            if (this.user['avatar'] != null && this.user['avatar'] != '') {
                this.userAvatar = this.userStore.userStoreValue.avatar;
            }
        }
    }

    showPasswordForm() {
        this.show_password_form = true;
    }

    closePasswordForm() {
        this.show_password_form = false;
        this.submittedPassword = false;
        this.submitted = false;
        this.fillUserData();
    }

    onSubmitPassword() {
        this.submittedPassword = true;
        if (!this.editPasswordForm.invalid) {
            this.updatePassword(
                this.editPasswordForm.value.old_password,
                this.editPasswordForm.value.new_password
            );
        }
    }

    updatePassword(old_password: string, new_password: string) {
        const data = {
            password: new_password,
            old_password,
        };
        const updatePasswordString = this.profile_feedback
            .successChangedPassword;
        const noMatchPasswordString = this.profile_feedback.passwordDoesntMatch;
        const updatePasswordErrorString = this.profile_feedback
            .somethingWentWrong;

        this._credentialsApiService.updateUser(this.user['id'], data).subscribe(
            (response) => {
                this.snackbar.setText(updatePasswordString);
                this.snackbar.setStatusClass('success');
                this.snackbar.activate();
                this.closePasswordForm();
            },
            (error) => {
                if (error.status == 400) {
                    this.snackbar.setText(noMatchPasswordString);
                } else {
                    this.snackbar.setText(updatePasswordErrorString);
                }
                this.snackbar.setStatusClass('error');
                this.snackbar.activate();
                console.log(error);
            }
        );
    }

    onSubmit() {
        this.submitted = true;
        if (!this.editUserForm.invalid) {
            this.editUser(
                this.editUserForm.value.username,
                this.editUserForm.value.email
            );
        }
    }

    editUser(username: string, email: string) {
        const data = {
            first_name: username,
            email,
        };
        let formData: FormData = new FormData();
        formData.append('first_name', username);
        formData.append('email', email);
        if (
            this.userAvatar != 'assets/images/fake-avatar.svg' &&
            this.imagePath != '' &&
            !this.helper.validUrl(this.userAvatar)
        ) {
            data['avatar'] = this.userAvatar;
            formData.append('avatar', this.avatarFile);
        }
        this._credentialsApiService
            .updateUser(this.user['id'], formData)
            .subscribe(
                (response) => {
                    this.snackbar.setStatusClass('success');
                    this.snackbar.setText(
                        this.profile_feedback.updateUserSuccess
                    );
                    this.snackbar.activate();
                    this.callback.emit(true);
                    this.close();
                },
                (error) => {
                    if (error.status === 500) {
                        this.snackbar.setText(
                            this.profile_feedback.somethingWentWrong
                        );
                    } else {
                        this.snackbar.setText(
                            this.profile_feedback.userEmailAlreadyExists
                        );
                        this.editUserForm.controls['email'].setErrors({
                            incorrect: true,
                        });
                    }
                    this.snackbar.setStatusClass('error');
                    this.snackbar.setAutoClose(false);
                    this.snackbar.activate();
                    console.log(error);
                }
            );
    }

    open() {
        this.show = true;
        this.show_password_form = false;
        this.snackbar.setAutoClose(true);
        this.fillUserData();
    }

    close() {
        this.show = false;
        this.submitted = false;
        this.submittedPassword = false;
    }

    /**
     * Waiting for a file preview
     * @param files
     */
    async getPreview(files) {
        this.avatarFile = files[0];
        let previewObject = <any>await this.helper.getImagePreview(files);
        this.errorFile = previewObject.error;
        this.imagePath = previewObject.imagePath;
        this.userAvatar = previewObject.preview;
    }

    getModalResult(event: boolean) {
        if (!event) {
            this.show = false;
        }
    }
}
